import { Link } from 'react-router-dom';
import { useCollection } from '@/hooks/useCollection';
import type { Leader } from '@/lib/types';

export default function LeadershipPreview() {
  const { rows: leaders, loading, error, reload } = useCollection<Leader>('leaders', 'sort_order', true);
  const featured = leaders.slice(0, 3);

  return (
    <section className="py-20 md:py-28 bg-background-100">
      <div className="max-w-[1200px] mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-accent-600 font-label text-sm tracking-widest uppercase font-semibold mb-3">
            Our Ministers &amp; Leaders
          </p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 leading-tight">
            Faithful Shepherds of Our Community
          </h2>
          <div className="w-12 h-1 bg-accent-500 mt-5 mx-auto" />
          <p className="text-foreground-500 mt-5 text-base leading-relaxed max-w-xl mx-auto">
            Meet the dedicated leaders who guide TGMCI with wisdom, compassion, and a heart for service.
          </p>
        </div>

        {loading ? (
          <p className="text-center text-foreground-400 py-10">
            <i className="ri-loader-4-line animate-spin inline-block" aria-hidden="true" /> Loading leaders...
          </p>
        ) : error ? (
          <div className="text-center py-10">
            <p className="text-red-600 text-sm mb-3">Could not load leaders.</p>
            <button type="button" onClick={reload} className="btn-outline">Retry</button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 md:gap-8">
            {featured.map((leader) => (
              <article key={leader.id} className="group text-center">
                <div className="relative mx-auto w-44 h-44 mb-5">
                  <div className="w-44 h-44 rounded-full overflow-hidden border-4 border-background-50 img-zoom">
                    <img
                      src={leader.image}
                      alt={leader.name}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-accent-500 text-foreground-950 text-xs font-label px-3 py-1 rounded-full whitespace-nowrap font-semibold">
                    {leader.title?.split(',')[0]}
                  </div>
                </div>
                <h3 className="font-heading font-semibold text-lg text-foreground-800 mt-4 mb-1">{leader.name}</h3>
                <p className="text-foreground-500 text-sm">{leader.title}</p>
                <p className="text-foreground-500 text-xs leading-relaxed mt-3 px-4 line-clamp-3">{leader.bio}</p>
              </article>
            ))}
          </div>
        )}

        <div className="text-center mt-10">
          <Link to="/about" className="btn-outline">
            Meet All Leaders
            <i className="ri-arrow-right-line" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>
  );
}